import { Card, Col, DatePicker, Empty, Row, Select, Statistic, Table, Tag, Typography } from 'antd';
import type { TableProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { inventoryApi, type VendorLedgerRow } from '@/api/inventory';
import { PageHeader } from '@/shared/components/page-header';
import { ExportButton } from '@/shared/components/ui';

const PKR = (n: string | number) => Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

export function VendorLedgerPage() {
  const [vendorId, setVendorId] = useState<number | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('year'), dayjs()]);

  const vendorsQuery = useQuery({
    queryKey: ['inventory', 'vendors'],
    queryFn: () => inventoryApi.listVendors(),
  });

  const ledgerQuery = useQuery({
    queryKey: ['inventory', 'vendor-ledger', vendorId, range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD')],
    queryFn: () =>
      inventoryApi.vendorLedger(vendorId!, range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD')),
    enabled: !!vendorId,
  });

  const data = ledgerQuery.data;

  const columns: TableProps<VendorLedgerRow>['columns'] = [
    { title: 'Date', dataIndex: 'date', width: 110, render: (d: string) => dayjs(d).format('D MMM YYYY') },
    {
      title: 'Type',
      dataIndex: 'type',
      width: 170,
      render: (v: string) => {
        const lower = v.toLowerCase();
        const color = lower.includes('reversed')
          ? 'purple'
          : lower.includes('payment')
          ? 'green'
          : lower.includes('credit')
          ? 'gold'
          : 'default';
        return <Tag color={color}>{v}</Tag>;
      },
    },
    { title: 'Reference', dataIndex: 'reference', width: 140 },
    { title: 'Description', dataIndex: 'description', ellipsis: true },
    {
      title: 'Debit (paid)',
      dataIndex: 'debit',
      width: 130,
      align: 'right' as const,
      render: (v: string) => (Number(v) > 0 ? PKR(v) : <Typography.Text type="secondary">—</Typography.Text>),
    },
    {
      title: 'Credit (purchase)',
      dataIndex: 'credit',
      width: 130,
      align: 'right' as const,
      render: (v: string) => (Number(v) > 0 ? PKR(v) : <Typography.Text type="secondary">—</Typography.Text>),
    },
    {
      title: 'Payable',
      dataIndex: 'runningPayable',
      width: 140,
      align: 'right' as const,
      render: (v: string) => <Typography.Text strong>₨ {PKR(v)}</Typography.Text>,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <PageHeader
        eyebrow="Inventory / Vendors"
        title="Vendor ledger"
        description="Chronological purchases and payments for one vendor with running payable balance."
        actions={
          <ExportButton
            items={[{
              kind: 'xlsx',
              url: '/reports/exports/vendor-ledger.xlsx',
              params: vendorId
                ? { vendorId, from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') }
                : {},
              fileName: `vendor-ledger-${vendorId}-${range[0].format('YYYY-MM-DD')}-to-${range[1].format('YYYY-MM-DD')}.xlsx`,
              disabled: !data || !vendorId,
            }]}
          />
        }
      />

      <Card>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={10}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Vendor
            </Typography.Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Choose vendor"
              loading={vendorsQuery.isLoading}
              value={vendorId}
              onChange={setVendorId}
              options={(vendorsQuery.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
              showSearch
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} md={10}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Period
            </Typography.Text>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              value={range}
              onChange={(v) => v && v[0] && v[1] && setRange([v[0], v[1]])}
              allowClear={false}
            />
          </Col>
        </Row>
      </Card>

      {data && (
        <Card>
          <Row gutter={[16, 12]}>
            <Col xs={12} md={6}>
              <Statistic title="Opening payable" value={`₨ ${PKR(data.openingPayable)}`} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Period purchases"
                value={`₨ ${PKR(data.totalCredits)}`}
                styles={{ content: { color: '#d4380d' } }}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Period payments"
                value={`₨ ${PKR(data.totalDebits)}`}
                styles={{ content: { color: '#389e0d' } }}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Closing payable"
                value={`₨ ${PKR(data.closingPayable)}`}
                styles={{ content: { color: Number(data.closingPayable) > 0 ? '#cf1322' : undefined } }}
              />
            </Col>
          </Row>
        </Card>
      )}

      <Card>
        {!data ? (
          <Empty description="Choose a vendor to load the ledger." />
        ) : data.rows.length === 0 ? (
          <Empty description="No transactions in this period" />
        ) : (
          <Table<VendorLedgerRow>
            rowKey={(_, i) => String(i)}
            dataSource={data.rows}
            loading={ledgerQuery.isFetching}
            columns={columns}
            pagination={{ pageSize: 50, showSizeChanger: true }}
            size="small"
            scroll={{ x: 1100 }}
          />
        )}
      </Card>
    </div>
  );
}
